/**
 * 🔒 The gear on a page a setting governs (decision 2805; T1614). The owner, on
 * the pictures page of 2026-10-08 (H15): *"Yes, build the gear on the Items
 * list, Expenses, the Work Order page and the Deal page."* One gear stands at
 * the right end of the page's toolbar row and opens a menu of the entries that
 * govern the page, each a link to where it is edited. 🔨 Settings stays the one
 * place a setting is edited: the gear leads there and edits nothing itself.
 */
import type { StringKey } from '../strings';
import { KB_CHECKLISTS_ROUTE } from '../knowledge/model';
import { ADMIN_OPERATIONS_CAPABILITY } from '../permissions/approvals';

/** One entry of a page's gear menu, in the order the menu lists it. */
export interface PageSettingsEntry {
  /**
   * Where the entry leads: a Settings entry's Index route, with a section's
   * anchor after `#` where the setting is one section of a grouped page.
   */
  route: string;
  /**
   * A name of its own, where the Index row's title would not name it: a section
   * of a grouped page, or the one page outside Settings no Index row carries.
   * Absent, the menu reads the Index row's title.
   */
  labelKey?: StringKey;
  /**
   * 🔒 For the one entry outside Settings, which no Index row carries: the
   * capability the page it opens asks for. The entry is listed only where the
   * Index served this person a row gated on that same key, which is the
   * server's own proof the person holds it. An entry outside Settings naming
   * none is never listed.
   */
  capabilityKey?: string;
}

/**
 * 🔒 The four pages the owner named and the entries each lists (decision 2805).
 * 🔨 Written once here: the list and record specs name these, and the Expenses
 * page reads its own, so no route is typed twice.
 */
export const PAGE_SETTINGS_ENTRIES = {
  itemList: [
    { route: '/settings/catalog/categories' },
    { route: '/settings/catalog/attribute-types' },
    { route: '/settings/catalog/pricing-defaults' },
  ],
  /** A section of the grouped Expenses settings page (decision 0584), so the link carries the section's anchor and the section's own name. */
  expenses: [{ route: '/settings/accounting/expense-categories#expense-categories', labelKey: 'settings.expenseCategories.title' }],
  /**
   * 🔒 Checklist Templates are edited on the Knowledge section's Checklists tab
   * (decision 2281), so this one entry leads outside Settings. That page and its
   * routes ask the administrator's capability, `admin.operations`, and a person
   * without it reads Access Denied there, so the entry is gated on the same key:
   * it is listed where the Index served a row carrying it, such as Priority
   * Tiers beside it on this menu.
   */
  workOrder: [
    { route: '/settings/accounting/priority-tiers' },
    { route: KB_CHECKLISTS_ROUTE, labelKey: 'kb.checklists.title', capabilityKey: ADMIN_OPERATIONS_CAPABILITY },
  ],
  deal: [{ route: '/settings/crm/stage-actions' }, { route: '/settings/crm/stage-questions' }, { route: '/settings/crm/discovery-templates' }],
} as const satisfies Readonly<Record<string, readonly PageSettingsEntry[]>>;

/** The page an entry's route opens, without the section's anchor: what an Index row's route is compared with. */
export function pageSettingsPath(route: string): string {
  return route.split('#')[0] ?? route;
}
